import { useState } from "react";
import { PREMIUM_FEATURES } from "@common/flatRateBilling";
import {
    BANK_CASH_ITEM_TYPE_LABELS,
    BankCashItemType,
} from "@common/types/bankCashItem";
import type { TooltipTriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { useAllowFeature } from "@hooks/useAllowFeature";
import { useOverlays } from "contexts/OverlayContext";

export const TRANSACTION_TYPE = "transaction";

type AddTransactionType = typeof TRANSACTION_TYPE | BankCashItemType;

const TYPE_ITEMS: Array<PickerItemData<AddTransactionType>> = [
    { value: TRANSACTION_TYPE, label: "Transaction" },
    ...Object.values(BankCashItemType).map((type) => ({
        value: type,
        label: BANK_CASH_ITEM_TYPE_LABELS[type],
    })),
];

export function useCanAddCashItems(): boolean {
    const { hasAccess: hasReconciliationAccess } = useAllowFeature(
        PREMIUM_FEATURES.RECONCILIATION,
    );
    return hasReconciliationAccess;
}

export function useOpenAddTransactionType() {
    const { openOverlay } = useOverlays();

    return (type: AddTransactionType, financialAccountId?: number) => {
        if (type === BankCashItemType.CHECK) {
            openOverlay("recordCheck", { financialAccountId });
            return;
        }
        if (type === BankCashItemType.DEPOSIT) {
            openOverlay("recordDeposit", { financialAccountId });
            return;
        }
        openOverlay("addTransactions", true);
    };
}

interface Props {
    value: AddTransactionType;
    onValueChange: (type: AddTransactionType) => void;
    unsavedItemLabel?: string;
    onBlur?: () => void;
    invalid?: boolean;
    buttonProps?: TooltipTriggerButtonProps;
}

function isOtherForm(from: AddTransactionType, to: AddTransactionType) {
    return (from === TRANSACTION_TYPE) !== (to === TRANSACTION_TYPE);
}

export const AddTransactionTypePicker = ({
    value,
    onValueChange,
    unsavedItemLabel,
    onBlur,
    invalid,
    buttonProps,
}: Props) => {
    const [pendingType, setPendingType] = useState<AddTransactionType>();

    return (
        <>
            <Picker.Select
                items={TYPE_ITEMS}
                value={value}
                onValueChange={(type) => {
                    if (type === null || type === value) {
                        return;
                    }
                    if (unsavedItemLabel && isOtherForm(value, type)) {
                        setPendingType(type);
                        return;
                    }
                    onValueChange(type);
                }}
                onBlur={onBlur}
                aria-invalid={invalid}
                trigger={<Picker.Button variant="primary" {...buttonProps} />}
            />
            {pendingType && unsavedItemLabel ? (
                <ConfirmationModal
                    show
                    title={`Discard this ${unsavedItemLabel}?`}
                    question="Switching the type closes this form and discards what you entered."
                    yes="Discard and switch"
                    no="Keep editing"
                    yesBtnVariant="destructive"
                    onConfirm={() => onValueChange(pendingType)}
                    onReject={() => setPendingType(undefined)}
                    onHide={() => setPendingType(undefined)}
                />
            ) : null}
        </>
    );
};
